import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";

const ProfilePage = ({ verifiedEmail, isVerified, onLogout }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!verifiedEmail) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/manageProfile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "get", email: verifiedEmail })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || "Unable to load profile");
        if (!cancelled) {
          setFirstName(data.firstName || "");
          setLastName(data.lastName || "");
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verifiedEmail]);

  if (!isVerified || !verifiedEmail) {
    return <Navigate to="/login" replace />;
  }

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/manageProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update",
          email: verifiedEmail,
          firstName,
          lastName
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to save");
      setStatus("Profile saved.");
    } catch (err) {
      setError(err.message || "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Delete your Ahavibes account? You will lose access immediately. Product emails will stop. This cannot be undone from the app."
    );
    if (!confirmed) return;
    setDeleting(true);
    setError("");
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/manageProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete", email: verifiedEmail })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to delete account");
      if (typeof onLogout === "function") onLogout();
    } catch (err) {
      setError(err.message || "Unable to delete account");
      setDeleting(false);
    }
  };

  return (
    <PageContainer className="profile-page">
      <h1>Profile</h1>
      <p>
        Update your name. Email is used to sign in and process insights. See{" "}
        <Link to="/faq">FAQ</Link> for product emails and deletion.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <form className="profile-page__form" onSubmit={handleSave}>
          <label className="profile-page__label">
            First name
            <input
              className="profile-page__input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              maxLength={80}
            />
          </label>
          <label className="profile-page__label">
            Last name
            <input
              className="profile-page__input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              maxLength={80}
            />
          </label>
          <label className="profile-page__label">
            Email
            <input className="profile-page__input" value={verifiedEmail} disabled readOnly />
          </label>
          <div className="profile-page__actions">
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}

      {status ? <p className="profile-page__status">{status}</p> : null}
      {error ? (
        <p className="profile-page__status profile-page__status--error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="profile-page__danger">
        <h2>Delete account</h2>
        <p>
          Deletes access to Ahavibes for this email, stops product emails, and signs you out. Login codes will no longer
          restore this account.
        </p>
        <button
          type="button"
          className="profile-page__delete"
          onClick={handleDelete}
          disabled={deleting || loading}
        >
          {deleting ? "Deleting…" : "Delete account"}
        </button>
      </div>
    </PageContainer>
  );
};

export default ProfilePage;
